import { useQuery, useQueryClient } from '@tanstack/react-query'

import { consulta, pedir } from './nucleo'
import type {
  AgenciaSocia,
  Cambio,
  CuerpoDeFichaDeAgencia,
  EnlaceDeSocio,
  EspacioConPlan,
  EstadoDeGeneracion,
  EstadoDeSuscripcion,
  Facturacion,
  Historial,
  HistorialDeCreditos,
  Lista,
  Movimiento,
  ResultadoDeCaptura,
  ResumenDeCreditos,
  ResumenDeSocio,
  Saldo,
  Sitio,
  Version,
} from './tipos'

/**
 * Las lecturas del panel. Dos reglas:
 *
 * - **Se filtra siempre por sitio o por cliente**, en la propia petición. El acceso lo
 *   acota el núcleo (§4, §5), pero pedir «todo lo que puedo ver» y filtrar aquí sería
 *   traerse la web de al lado al navegador para esconderla con CSS.
 * - **La clave de caché lleva el sitio**: cambiar de web no puede enseñar por un
 *   instante los datos de la anterior.
 */

export const useEspacio = (espacio: number) =>
  useQuery({
    queryKey: ['espacio', espacio],
    queryFn: () => pedir<EspacioConPlan>(`/clientes/${espacio}${consulta({ depth: 1 })}`),
  })

export const useSitios = (espacio: number) =>
  useQuery({
    queryKey: ['sitios', espacio],
    queryFn: async () => {
      const lista = await pedir<Lista<Sitio>>(`/sitios${consulta({ 'where[cliente][equals]': espacio, limit: 100, sort: 'nombre', depth: 0 })}`)
      return lista.docs
    },
  })

export const useSaldo = (espacio: number) =>
  useQuery({
    queryKey: ['saldo', espacio],
    queryFn: () => pedir<Saldo>(`/creditos/saldo${consulta({ cliente: espacio })}`),
  })

/** El saldo por bolsa, el ciclo y en qué se ha gastado (plan del CMS dinámico §9). Sin coste ni margen: el núcleo no los da. */
export const useResumenDeCreditos = (espacio: number) =>
  useQuery({
    queryKey: ['saldo', espacio, 'resumen'],
    queryFn: () => pedir<ResumenDeCreditos>(`/creditos/resumen${consulta({ cliente: espacio })}`),
  })

/** Cada llamada a la IA, de 25 en 25, la más nueva primero. */
export const useHistorialDeCreditos = (espacio: number, pagina: number) =>
  useQuery({
    queryKey: ['saldo', espacio, 'historial', pagina],
    queryFn: () => pedir<HistorialDeCreditos>(`/creditos/historial${consulta({ cliente: espacio, pagina })}`),
    placeholderData: (anterior) => anterior,
  })

export const useMovimientos = (espacio: number) =>
  useQuery({
    queryKey: ['movimientos', espacio],
    queryFn: async () => {
      const lista = await pedir<Lista<Movimiento>>(
        `/movimientos-de-credito${consulta({ 'where[cliente][equals]': espacio, limit: 40, sort: '-fecha', depth: 0 })}`,
      )
      return lista.docs
    },
  })

// --- Lo que la cuenta paga a Wixy (CLAUDE.md §31) --------------------------------------

/** Planes, paquetes, la suscripción y los pagos. Solo del propietario (o del equipo): a los demás, 403. */
export const useFacturacion = (espacio: number, activa = true) =>
  useQuery({
    queryKey: ['facturacion', espacio],
    queryFn: () => pedir<Facturacion>(`/plataforma/facturacion${consulta({ cliente: espacio })}`),
    enabled: activa,
  })

/**
 * Pagar con PayPal. Como `socio`, funciones planas: la pantalla lleva el estado. Empezar
 * (suscribirse, comprar) devuelve la dirección de PayPal donde el cliente aprueba el pago;
 * al volver, `confirmar` o `capturar` lo cierran. Un 502 trae lo que dijo PayPal.
 */
export const facturacion = (espacio: number) => ({
  suscribirse: (plan: number) => pedir<{ aprobar: string; suscripcion: string }>('/plataforma/suscripciones', { metodo: 'POST', cuerpo: { cliente: espacio, plan } }),
  confirmar: (suscripcion: string) =>
    pedir<{ estado: EstadoDeSuscripcion }>('/plataforma/suscripciones/confirmar', { metodo: 'POST', cuerpo: { cliente: espacio, suscripcion } }),
  cancelar: () => pedir<{ hasta: null | string }>('/plataforma/suscripciones/cancelar', { metodo: 'POST', cuerpo: { cliente: espacio } }),
  comprar: (paquete: number) => pedir<{ aprobar: string; orden: string }>('/plataforma/creditos/comprar', { metodo: 'POST', cuerpo: { cliente: espacio, paquete } }),
  capturar: (orden: string) => pedir<ResultadoDeCaptura>('/plataforma/creditos/capturar', { metodo: 'POST', cuerpo: { cliente: espacio, orden } }),
})

/** Lo que hay que olvidar después de pagar: el saldo, sus movimientos, la facturación y el plan de la cuenta. */
export const useRefrescarFacturacion = (espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  return () => {
    for (const clave of ['facturacion', 'saldo', 'movimientos', 'espacio']) void clienteDeConsultas.invalidateQueries({ queryKey: [clave, espacio] })
  }
}

// --- El programa de socios ------------------------------------------------------------

/** El resumen entero: saldo, embudo, referidos y pagos. El núcleo barre lo suyo antes de responder. */
export const useSocio = (espacio: number, activa = true) =>
  useQuery({
    queryKey: ['socio', espacio],
    queryFn: () => pedir<ResumenDeSocio>(`/socios/yo${consulta({ cliente: espacio })}`),
    enabled: activa,
  })

/** Lo ligero: el código y el enlace, para el chip de la cabecera. No barre nada. */
export const useEnlaceDeSocio = (espacio: number, activa = true) =>
  useQuery({
    queryKey: ['socio-enlace', espacio],
    queryFn: () => pedir<EnlaceDeSocio>(`/socios/yo/enlace${consulta({ cliente: espacio })}`),
    enabled: activa,
    staleTime: 5 * 60_000,
  })

/**
 * Lo que escribe el socio. Como `diseno`, funciones planas: la pantalla lleva el
 * estado y refresca con `useRefrescarSocio`.
 */
export const socio = (espacio: number) => ({
  guardarPaypal: (paypalEmail: string) => pedir<{ ok: true; paypalEmail: string }>('/socios/yo/paypal', { metodo: 'PUT', cuerpo: { cliente: espacio, paypalEmail } }),
  pedirPago: (paypalEmail?: string) =>
    pedir<{ ok: true; pago: { id: number; importe: number; estado: string; paypalEmail: string } }>('/socios/yo/pagos', {
      metodo: 'POST',
      cuerpo: { cliente: espacio, paypalEmail },
    }),
  /** Pedir entrar en el programa de agencias con la ficha entera. Un 400 dice en `datos.campo` qué campo falla. */
  solicitarAgencia: (ficha: CuerpoDeFichaDeAgencia) =>
    pedir<{ ok: true; agencia: AgenciaSocia }>('/socios/yo/agencia', { metodo: 'POST', cuerpo: { cliente: espacio, ...ficha } }),
  /** Editar la ficha mientras está en revisión o ya aprobada (un 409 si no). El mismo cuerpo que al pedir. */
  editarAgencia: (ficha: CuerpoDeFichaDeAgencia) =>
    pedir<{ ok: true; agencia: AgenciaSocia }>('/socios/yo/agencia', { metodo: 'PUT', cuerpo: { cliente: espacio, ...ficha } }),
  /** Con la cookie que dejó el enlace de un socio: nunca falla con 4xx salvo por la sesión. */
  atribuir: (codigo: string) =>
    pedir<{ ok: true; codigo: string } | { ok: false; motivo: string }>('/socios/atribuir', { metodo: 'POST', cuerpo: { cliente: espacio, codigo } }),
})

/** Lo que hay que olvidar después de tocar algo del programa: el resumen y el chip. */
export const useRefrescarSocio = (espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  return () => {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['socio', espacio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['socio-enlace', espacio] })
  }
}

export const useVersiones = (sitio: number, opciones: { refrescarCada?: false | number } = {}) =>
  useQuery({
    queryKey: ['versiones', sitio],
    queryFn: async () => {
      const lista = await pedir<Lista<Version>>(`/versiones-de-tema${consulta({ 'where[sitio][equals]': sitio, limit: 40, sort: '-numero', depth: 0 })}`)
      return lista.docs
    },
    refetchInterval: opciones.refrescarCada ?? false,
  })

/**
 * La historia del diseño **para quien lo diseña** (`GET /versiones-de-tema/historial`): la
 * lee también un editor con permiso de diseño, que en el listado de la colección no ve nada.
 * Es además el sondeo mientras una versión se construye. La clave es `versiones`, la misma
 * que olvida `useRefrescarDiseno`.
 */
export const useHistorial = (sitio: number, opciones: { refrescarCada?: false | number; activo?: boolean } = {}) =>
  useQuery({
    queryKey: ['versiones', sitio, 'historial'],
    queryFn: () => pedir<Historial>(`/versiones-de-tema/historial${consulta({ sitio })}`),
    // Con una generación en marcha (de quien mira o de otra persona de la cuenta) se mira de
    // vez en cuando: cuando termine, aparece sola y «Generar» vuelve a estar disponible.
    refetchInterval: (q) => opciones.refrescarCada || (q.state.data?.enMarcha ? 10_000 : false),
    enabled: opciones.activo ?? true,
    retry: false,
  })

/** Cómo va una generación en cola. Se sondea mientras está «en curso». */
export const useGeneracion = (sitio: number, generacion: null | number) =>
  useQuery({
    queryKey: ['generacion', sitio, generacion],
    queryFn: () => pedir<EstadoDeGeneracion>(`/versiones-de-tema/generacion${consulta({ sitio, id: generacion ?? undefined })}`),
    enabled: generacion !== null,
    refetchInterval: (q) => (q.state.data?.estado === 'en-curso' || !q.state.data ? 3000 : false),
    retry: 2,
  })

/**
 * El contenido que la web tiene publicado (lo cuenta el núcleo, por web). Publicar el
 * diseño no publica páginas y la previa enseña lo publicado (§13): con esto la pantalla
 * avisa antes de que una web recién creada salga en «Esta página no existe».
 */
export type ContenidoDeLaWeb = {
  paginasPublicadas: number
  paginasEnBorrador: number
  inicioPublicado: boolean
  productosPublicados: number
  productosEnBorrador: number
  /**
   * Las que nunca se han publicado (como mucho 60, lo mismo que se publica de una vez), con si
   * les queda algún texto «[Por rellenar: …]» y cuál es la portada.
   */
  borradores: PaginaEnBorrador[]
  /**
   * Las **ya publicadas** que conservan textos «[Por rellenar: …]», mientras la web no está en
   * internet: la primera publicación del diseño las sacaría así. Opcional solo para no romper
   * con un núcleo de antes.
   */
  publicadasPorRellenar?: Array<{ id: number; titulo: string }>
}

/**
 * `seccionesVacias`: las secciones que saldrían con su título y nada debajo (una lista sin
 * ninguna fila), por su nombre. `portada` y `seccionesVacias` son opcionales solo para no
 * romper con un núcleo de antes.
 */
export type PaginaEnBorrador = { id: number; titulo: string; slug: string; porRellenar: boolean; portada?: boolean; seccionesVacias?: string[] }

export type EstadoDelDiseno = {
  sitio: number
  cliente: number
  publicada: null | number
  anterior: null | number
  flujo: 'aprobacion' | 'directa'
  ultima: null | { numero: number; estado: string; comentario: string; registro: null | string }
  /** Nulo si el núcleo no ha podido contarlo (la pantalla sigue, sin el aviso); opcional para no romper con un núcleo de antes. */
  contenido?: ContenidoDeLaWeb | null
}

// Publicar (contenido y diseño) va por «Publicar mi web»: `api/publicacion.ts`.

export const useEstadoDelDiseno = (sitio: number) =>
  useQuery({
    queryKey: ['diseno-estado', sitio],
    queryFn: () => pedir<EstadoDelDiseno>(`/versiones-de-tema/estado${consulta({ sitio })}`),
  })

/** Las páginas de un sitio, para elegir alcance «esta página». Solo título y slug. */
export const usePaginas = (sitio: number) =>
  useQuery({
    queryKey: ['paginas', sitio],
    queryFn: async () => {
      const lista = await pedir<Lista<{ id: number; titulo?: string; slug?: string }>>(
        `/paginas${consulta({ 'where[sitio][equals]': sitio, limit: 100, sort: 'titulo', depth: 0 })}`,
      )
      return lista.docs.map((p) => ({ id: p.id, titulo: p.titulo || p.slug || `Página ${p.id}` }))
    },
  })

// --- Lo que escribe -------------------------------------------------------------------

export type Generacion = {
  version: number
  explicacion?: string
  cambios: Cambio[]
  noAplicado?: Array<{ ruta: string; motivo: string }>
  accionesPropuestas?: Array<{ bloque?: string; pagina?: string; motivo?: string }>
  necesitaDato?: null | string
  creditos: { consumidos: number; saldo: number; aviso?: null | string }
  modelo?: string
  reintentos?: number
  conflicto?: null | { cabeza: number; desde: number }
}

export type Estimacion = { creditos: number; saldo: number; alcanza: boolean; modelo: string }

/**
 * Las acciones de diseño son **funciones planas**, no mutaciones sueltas: el ciclo real
 * es una secuencia —estimar, confirmar, generar, esperar a que construya, pedir la
 * previa— y partirla en seis mutaciones independientes obliga a encadenarlas con
 * `onSuccess` anidados, que es la forma de que el orden deje de leerse. La pantalla
 * lleva su propio estado y llama a esto; lo que hay que refrescar lo dice `refrescar`.
 */
type Objetivo = { alcance: string; bloque?: string; pagina?: number; prompt: string; /** Sobre qué versión: por omisión, la última que construye. */ desde?: number }

export const diseno = (sitio: number) => ({
  estimar: (objetivo: Objetivo) => pedir<Estimacion>('/versiones-de-tema/estimar', { metodo: 'POST', cuerpo: { ...objetivo, sitio } }),
  generar: (objetivo: Objetivo) => pedir<Generacion>('/versiones-de-tema/generar', { metodo: 'POST', cuerpo: { ...objetivo, sitio } }),
  /**
   * Generar **en cola**: responde enseguida con el número de la generación, y la pantalla la
   * sigue con `useGeneracion`. Una generación puede pasar de los 100 s que Cloudflare da a una
   * respuesta: esperándola, el cliente leía un error mientras se le cobraba.
   */
  generarEnCola: (objetivo: Objetivo) => pedir<EstadoDeGeneracion>('/versiones-de-tema/generar', { metodo: 'POST', cuerpo: { ...objetivo, sitio, enCola: true } }),
  revertir: () => pedir<{ anterior: number; publicada: number }>('/versiones-de-tema/revertir', { metodo: 'POST', cuerpo: { sitio } }),
  /** Junta una versión con otra (la publicada) si tocan ficheros distintos (§18). Un 409 dice cuáles coinciden. */
  fusionar: (version: number, sobre: number) => pedir<{ version: number }>('/versiones-de-tema/fusionar', { metodo: 'POST', cuerpo: { sitio, version, sobre } }),
  previa: (version?: number) => pedir<{ caduca: string; url: string; version: number }>('/versiones-de-tema/previa', { metodo: 'POST', cuerpo: { sitio, version } }),
  descartar: (version: number) => pedir<{ ultimaViva: number; version: number }>('/versiones-de-tema/descartar', { metodo: 'POST', cuerpo: { sitio, version } }),
  publicar: (version: number, esperada: number) =>
    pedir<{ anterior: number; publicada: number }>('/versiones-de-tema/publicar', { metodo: 'POST', cuerpo: { sitio, version, esperada } }),
  solicitar: (version: number, mensaje?: string) =>
    pedir<{ solicitud: number; version: number }>('/versiones-de-tema/solicitar', { metodo: 'POST', cuerpo: { sitio, version, mensaje } }),
  restaurar: (version: number) => pedir<{ version: number }>('/versiones-de-tema/restaurar', { metodo: 'POST', cuerpo: { sitio, version } }),
  construir: (version: number) => pedir<{ estado: string; version: number }>('/versiones-de-tema/construir', { metodo: 'POST', cuerpo: { sitio, version } }),
  comparar: (desde: number, hasta: number) => pedir<{ cambios: Cambio[] }>(`/versiones-de-tema/comparar${consulta({ sitio, desde, hasta })}`),
  /** En qué estado está una versión concreta: es lo que se sondea mientras construye. */
  estadoDe: async (version: number): Promise<null | string> => {
    const lista = await pedir<Lista<Version>>(
      `/versiones-de-tema${consulta({ 'where[sitio][equals]': sitio, 'where[numero][equals]': version, limit: 1, depth: 0 })}`,
    )
    return lista.docs[0]?.estado ?? null
  },
})

/** Lo que hay que olvidar después de tocar el diseño: el historial, el estado y el saldo. */
export const useRefrescarDiseno = (sitio: number, espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  return () => {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['versiones', sitio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado', sitio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['saldo', espacio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['movimientos', espacio] })
  }
}
